import { AiFillCheckCircle } from "solid-icons/ai";
import { type Component, createUniqueId, Match, Show, Switch } from "solid-js";
import { useI18n } from "@/api/i18n";
import { badgeVariants } from "@/components/ui/badge";
import { DialogTrigger } from "@/components/ui/dialog";
import { cn } from "@/libs/cn";

/** The badge of a hub card: «Da completare», «Completato», «Da controllare». */
export type HubCardStatus = "todo" | "done" | "review";

export type HubCardProps = {
  icon: Component<{ class?: string }>;
  title: string;
  description: string;
  status: HubCardStatus;
  /**
   * A line under the description: what the card already holds. `proposal` is
   * a suggestion still to be checked (the sparkle), `muted` a summary.
   */
  detail?: { text: string; tone: "proposal" | "muted" };
};

/**
 * A card of the «Ci siamo quasi!» hub (AC23, D13): the `Trigger` of the
 * `Dialog` around it, rendered as a real full-width `<button>`, so mouse,
 * Enter and Space open it and Kobalte gives focus back to it on close.
 *
 * Its accessible name is the title only (`aria-labelledby`); the status, the
 * description and the detail line come as its description (OQ8). It wraps text
 * and a badge, never another control (R11): every element inside is a `span`,
 * phrasing content as a button requires.
 */
export const HubCard = (props: HubCardProps) => {
  const { t } = useI18n();
  const uid = createUniqueId();
  const ids = {
    title: `hub-card-${uid}-title`,
    status: `hub-card-${uid}-status`,
    description: `hub-card-${uid}-description`,
    detail: `hub-card-${uid}-detail`,
  };
  const complete = () => props.status === "done";
  const describedBy = () =>
    [ids.status, ids.description, ...(props.detail ? [ids.detail] : [])].join(
      " ",
    );

  return (
    <DialogTrigger
      as="button"
      type="button"
      aria-labelledby={ids.title}
      aria-describedby={describedBy()}
      class="block w-full cursor-pointer rounded-xl border bg-card p-6 text-left text-card-foreground shadow-none transition-[box-shadow,border-color,background-color] hover:shadow-sm focus-visible:outline-hidden focus-visible:ring-[1.5px] focus-visible:ring-ring focus-visible:ring-offset-2"
      classList={{
        "border-green-200": complete(),
        "bg-white border-gray-200": !complete(),
      }}
    >
      <span class="flex items-center gap-4">
        <span class="relative hidden shrink-0 md:block">
          <span class="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <props.icon class="h-5 w-5 text-primary" />
          </span>
          <Show when={complete()}>
            <span class="absolute -right-1 -top-1">
              <AiFillCheckCircle class="h-5 w-5 rounded-full bg-white text-green-600" />
            </span>
          </Show>
        </span>
        <span class="block min-w-0 flex-1">
          <span class="mb-1 flex w-full items-center justify-between gap-2">
            <span id={ids.title} class="text-base font-semibold">
              {props.title}
            </span>
            <span
              id={ids.status}
              class={cn(
                badgeVariants({
                  variant: complete() ? "success" : "warning",
                  size: "sm",
                }),
                "shrink-0",
              )}
            >
              <Switch>
                <Match when={props.status === "done"}>
                  {t("onboarding.linkedinHub.status.done")}
                </Match>
                <Match when={props.status === "review"}>
                  {t("onboarding.linkedinHub.sector.review")}
                </Match>
                <Match when={props.status === "todo"}>
                  {t("onboarding.linkedinHub.status.todo")}
                </Match>
              </Switch>
            </span>
          </span>
          <span id={ids.description} class="block text-sm">
            {props.description}
          </span>
          <Show when={props.detail}>
            {(detail) => (
              <span
                id={ids.detail}
                class="mt-2 flex items-start gap-1 text-xs"
                classList={{
                  // `--ring`, not `text-primary` (4.29:1 on white): AA for 12 px text.
                  "text-ring": detail().tone === "proposal",
                  "text-muted-foreground": detail().tone === "muted",
                }}
              >
                <Show when={detail().tone === "proposal"}>
                  <i
                    class="ph-fill ph-sparkle mt-0.5 shrink-0"
                    aria-hidden="true"
                  />
                </Show>
                <span>{detail().text}</span>
              </span>
            )}
          </Show>
        </span>
      </span>
    </DialogTrigger>
  );
};
